/**
 * Utility functions for screening interviews based on candidate resume/profile data
 */

import { API, graphqlOperation } from "aws-amplify";
import { createInterviewSlot } from "../graphql/mutations";
import { getCandidateProfile } from "../graphql/queries";

/**
 * Extract resume data from candidate profile for screening interview
 * @param {Object} candidateProfile - The candidate profile object from database
 * @returns {Object} Structured resume data for backend
 */
export const extractResumeDataForScreening = (candidateProfile) => {
  if (!candidateProfile) {
    console.warn('⚠️ No candidate profile provided for screening');
    return null;
  }

  // Extract all relevant resume fields
  const resumeData = {
    // Personal Information
    fullName: candidateProfile.fullName || '',
    email: candidateProfile.email || '',
    mobileNumber: candidateProfile.mobileNumber || '',
    gender: candidateProfile.gender || '',
    currentCity: candidateProfile.currentCity || '',
    address: candidateProfile.address || '',
    
    // Professional Information
    roleApplyFor: candidateProfile.roleApplyFor || '',
    workStatus: candidateProfile.workStatus || '', // fresher, experienced, etc.
    workPreference: candidateProfile.workPreference || '',
    workMode: candidateProfile.workMode || [], // Remote, Hybrid, Office
    workCategory: candidateProfile.workCategory || [], // Internship, FullTime, PartTime
    
    // Skills and Experience
    keySkills: candidateProfile.keySkills || '',
    description: candidateProfile.description || '', // Self-description/summary
    educationDetails: candidateProfile.educationDetails || '',
    
    // Additional Information
    preferredSalary: candidateProfile.preferredSalary || '',
    linkedinURL: candidateProfile.linkedinURL || '',
    githubURL: candidateProfile.githubURL || '',
    resumeURL: candidateProfile.resumeURL || '',
    
    // Profile metadata
    candidateGoal: candidateProfile.candidateGoal || '',
    candidateData: candidateProfile.candidateData || null, // Any additional parsed data
  };

  // Parse keySkills into an array if it's a comma-separated string
  if (typeof resumeData.keySkills === 'string' && resumeData.keySkills) {
    resumeData.keySkillsArray = resumeData.keySkills
      .split(',')
      .map(skill => skill.trim())
      .filter(skill => skill.length > 0);
  } else {
    resumeData.keySkillsArray = [];
  }

  // Calculate experience level from workStatus
  resumeData.experienceLevel = determineExperienceLevel(resumeData.workStatus);

  console.log('📝 Extracted resume data for screening:', {
    fullName: resumeData.fullName,
    roleApplyFor: resumeData.roleApplyFor,
    keySkillsCount: resumeData.keySkillsArray.length,
    hasDescription: !!resumeData.description,
    experienceLevel: resumeData.experienceLevel
  });

  return resumeData;
};

/**
 * Determine experience level from work status
 * @param {string} workStatus - The candidate's work status
 * @returns {string} Experience level category
 */
const determineExperienceLevel = (workStatus) => {
  if (!workStatus) return 'unknown';
  
  const status = workStatus.toLowerCase();
  
  if (status.includes('fresher') || status.includes('fresh') || status.includes('0') || status.includes('entry')) {
    return 'fresher';
  } else if (status.includes('experienced') || status.includes('experience')) {
    // Try to extract years if mentioned
    const yearsMatch = status.match(/(\d+)/);
    if (yearsMatch) {
      const years = parseInt(yearsMatch[1], 10);
      if (years <= 2) return 'junior';
      if (years <= 5) return 'mid-level';
      if (years <= 10) return 'senior';
      return 'expert';
    }
    return 'experienced';
  }
  
  return 'unknown';
};

/**
 * Create a screening interview slot for first-time candidates
 * @param {Object} params - Parameters for creating the screening slot
 * @returns {Promise<Object>} Created interview slot
 */
export const createScreeningInterviewSlot = async ({
  candidateProfileID,
  candidateEmail,
  candidateProfile,
  numberOfQuestions = 5 // Default 5 questions for screening
}) => {
  if (!candidateProfileID || !candidateEmail) {
    throw new Error('candidateProfileID and candidateEmail are required');
  }

  const now = new Date();
  const endTime = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour from now

  // Create meeting config with screening flag
  const meetingConfig = {
    interviewType: 'screening',
    isScreeningRound: true,
    isCandidateInitiated: true,
    candidateName: candidateProfile?.fullName || 'Candidate',
    numberOfQuestions: numberOfQuestions,
    createdAt: now.toISOString(),
    // Include resume summary for quick reference
    candidateRole: candidateProfile?.roleApplyFor || 'Not specified',
    candidateSkills: candidateProfile?.keySkills || 'Not specified',
    candidateExperience: candidateProfile?.workStatus || 'Not specified',
  };

  const interviewSlotInput = {
    candidateProfileID: candidateProfileID,
    candidateEmail: candidateEmail,
    date: now.toISOString().split('T')[0], // AWSDate: YYYY-MM-DD
    startTime: now.toTimeString().split(' ')[0], // AWSTime: HH:MM:SS
    endTime: endTime.toTimeString().split(' ')[0], // AWSTime: HH:MM:SS
    status: 'ACTIVE',
    meetingLinkURL: '',
    meetingConfig: JSON.stringify(meetingConfig),
    candidateAttendance: false,
    botAttendance: false,
    jdID: null, // No JD for screening round - based on resume
    candidateProfileInterviewSlotsId: candidateProfileID
  };

  console.log('🎯 Creating screening interview slot:', interviewSlotInput);

  try {
    const response = await API.graphql(
      graphqlOperation(createInterviewSlot, { input: interviewSlotInput })
    );

    const createdSlot = response.data.createInterviewSlot;
    console.log('✅ Screening interview slot created:', createdSlot.id);
    
    return createdSlot;
  } catch (error) {
    console.error('❌ Failed to create screening interview slot:', error);
    throw error;
  }
};

/**
 * Fetch candidate profile with full details for screening
 * @param {string} candidateProfileId - The candidate profile ID
 * @returns {Promise<Object>} Full candidate profile
 */
export const fetchCandidateProfileForScreening = async (candidateProfileId) => {
  try {
    const response = await API.graphql(
      graphqlOperation(getCandidateProfile, { id: candidateProfileId })
    );
    
    return response.data.getCandidateProfile;
  } catch (error) {
    console.error('❌ Failed to fetch candidate profile for screening:', error);
    throw error;
  }
};

/**
 * Prepare screening interview start message
 * This creates the data structure to send to backend when starting a screening interview
 * @param {Object} params - Parameters for the screening interview
 * @returns {Object} Start interview message with screening data
 */
export const prepareScreeningStartMessage = ({
  interviewSlotId,
  sessionId,
  candidateEmail,
  candidateProfileId,
  candidateProfile,
  numberOfQuestions = 5
}) => {
  // Extract resume data
  const resumeData = extractResumeDataForScreening(candidateProfile);

  const startMessage = {
    // Standard interview fields
    type: 'start_interview',
    interview_id: interviewSlotId,
    interview_slot_id: interviewSlotId,
    session_id: sessionId,
    candidate_email: candidateEmail,
    candidate_profile_id: candidateProfileId,
    number_of_questions: numberOfQuestions,
    
    // Screening-specific flags
    is_screening_round: true,
    interview_type: 'screening',
    
    // Resume/Profile data for screening
    candidate_resume_data: resumeData ? {
      // Personal Info
      full_name: resumeData.fullName,
      email: resumeData.email,
      phone: resumeData.mobileNumber,
      location: resumeData.currentCity || resumeData.address,
      
      // Professional Info
      target_role: resumeData.roleApplyFor,
      experience_status: resumeData.workStatus,
      experience_level: resumeData.experienceLevel,
      
      // Skills and Background
      skills: resumeData.keySkillsArray,
      skills_string: resumeData.keySkills,
      description: resumeData.description,
      education: resumeData.educationDetails,
      
      // Work Preferences
      work_mode: resumeData.workMode,
      work_category: resumeData.workCategory,
      salary_expectation: resumeData.preferredSalary,
      
      // Links
      linkedin_url: resumeData.linkedinURL,
      github_url: resumeData.githubURL,
      resume_url: resumeData.resumeURL,
      
      // Additional context
      candidate_goal: resumeData.candidateGoal,
    } : null,
    
    // Instructions for the screening bot
    screening_instructions: {
      focus_areas: [
        'Verify the candidate\'s claimed skills and experience',
        'Assess communication skills',
        'Understand career goals and motivations',
        'Evaluate basic technical knowledge based on stated skills',
        'Check for consistency between resume claims and verbal responses'
      ],
      interview_style: 'conversational',
      question_types: ['behavioral', 'situational', 'skill-verification', 'career-goals']
    }
  };

  console.log('📋 Prepared screening start message:', {
    interview_id: startMessage.interview_id,
    is_screening_round: startMessage.is_screening_round,
    has_resume_data: !!startMessage.candidate_resume_data,
    skills_count: startMessage.candidate_resume_data?.skills?.length || 0,
    target_role: startMessage.candidate_resume_data?.target_role
  });

  return startMessage;
};

/**
 * Check if an interview is a screening round
 * @param {Object} interviewSlot - The interview slot object
 * @returns {boolean} True if it's a screening round
 */
export const isScreeningRound = (interviewSlot) => {
  if (!interviewSlot?.meetingConfig) return false;
  
  try {
    const config = typeof interviewSlot.meetingConfig === 'string'
      ? JSON.parse(interviewSlot.meetingConfig)
      : interviewSlot.meetingConfig;
    
    return config.isScreeningRound === true || config.interviewType === 'screening';
  } catch (error) {
    console.warn('⚠️ Failed to parse meeting config:', error);
    return false;
  }
};

/**
 * Store screening completion flag in localStorage
 * @param {string} candidateEmail - The candidate's email
 */
export const markScreeningCompleted = (candidateEmail) => {
  if (!candidateEmail) return;
  
  try {
    const key = `screening_completed_${candidateEmail.toLowerCase().trim()}`;
    localStorage.setItem(key, new Date().toISOString());
    console.log('✅ Marked screening as completed for:', candidateEmail);
  } catch (error) {
    console.warn('⚠️ Failed to mark screening as completed:', error);
  }
};

/**
 * Check if screening has been completed for a candidate
 * @param {string} candidateEmail - The candidate's email
 * @returns {boolean} True if screening has been completed
 */
export const hasCompletedScreening = (candidateEmail) => {
  if (!candidateEmail) return false;
  
  try {
    const key = `screening_completed_${candidateEmail.toLowerCase().trim()}`;
    return localStorage.getItem(key) !== null;
  } catch (error) {
    console.warn('⚠️ Failed to check screening completion:', error);
    return false;
  }
};

export default {
  extractResumeDataForScreening,
  createScreeningInterviewSlot,
  fetchCandidateProfileForScreening,
  prepareScreeningStartMessage,
  isScreeningRound,
  markScreeningCompleted,
  hasCompletedScreening
};
